import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faUser, faBriefcase, faIdCard } from '@fortawesome/free-solid-svg-icons';
import { ADMIN_URLS } from '../config/apiConfig';

function ViewMember() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchMember();
  }, [id]);
  
  const fetchMember = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(ADMIN_URLS.MEMBER_DETAIL(id), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch member details');
      }
      
      const data = await response.json();
      setMember(data);
      
    } catch (error) {
      console.error('Error fetching member details:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const getStatusStyle = (status) => {
    switch (status) {
      case 'active':
        return {
          bg: '#e6f7ee',
          color: '#0d6832',
          border: '1px solid #a3e2c4',
        };
      case 'pending':
        return {
          bg: '#fff8e6',
          color: '#805d00',
          border: '1px solid #ffe3a0',
        };
      case 'expired':
        return {
          bg: '#feeeef',
          color: '#b2223b',
          border: '1px solid #f9c0c5',
        };
      default:
        return {
          bg: '#f0f2f5',
          color: '#606770',
          border: '1px solid #dadde1',
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Helper function to safely capitalize the first letter of a string
  const capitalizeFirstLetter = (str) => {
    if (!str) return 'N/A';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getMembershipTypeLabel = (type) => {
    const types = {
      'primary_member': 'Primary Member',
      'associate_member': 'Associate Member',
      'student_membership': 'Student Member',
      'financial_collegium': 'Financial Collegium',
      'academic_collegium': 'Academic Collegium',
      'institutional_collegium': 'Institutional Collegium'
    };
    return types[type] || capitalizeFirstLetter(type);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="admin-loading-spinner"></div>
          <p>Loading member details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !member) {
    return (
      <AdminLayout>
        <div className="admin-error">
          <div className="admin-error-icon">⚠️</div>
          <h3>Error: {error || 'Member not found'}</h3>
          <button 
            className="admin-btn admin-btn-primary" 
            onClick={() => navigate('/admin/members')}
          >
            Back to Members
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header" style={{ marginBottom: '24px' }}>
        <h1 className="admin-page-title">Member Details</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/members">Members</Link>
          </li>
          <li className="admin-breadcrumb-item active">{member.firstName} {member.lastName}</li>
        </ul>
      </div>
      
      <div className="admin-action-bar" style={{ marginBottom: '24px', display: 'flex', gap: '12px' }}>
        <Link
          to={`/admin/members/${id}/edit`}
          className="admin-btn admin-btn-primary admin-btn-with-icon"
          style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <FontAwesomeIcon icon={faEdit} />
          <span>Edit</span>
        </Link>
      </div>
      
      <div className="admin-cards-container">
        <div className="admin-card admin-card-elevated">
          <div className="admin-card-header admin-card-header-accent">
            <FontAwesomeIcon icon={faIdCard} className="admin-card-header-icon" />
            <h2 className="admin-card-title">Member Information</h2>
          </div>
          <div className="admin-card-body">
            <div className="admin-view-row admin-view-row-highlight" style={{ 
              display: 'flex', 
              alignItems: 'center',
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              marginBottom: '10px'
            }}>
              <div className="admin-view-label" style={{ 
                width: '150px', 
                fontWeight: '600',
                color: '#495057' 
              }}>Member ID</div>
              <div className="admin-view-value admin-id-value" style={{ 
                fontWeight: '500',
                color: '#343a40',
                backgroundColor: '#e9ecef',
                padding: '4px 10px',
                borderRadius: '4px',
                letterSpacing: '0.5px'
              }}>{member.membership_number}</div>
            </div>
            <div className="admin-view-row admin-view-row-highlight" style={{ 
              display: 'flex', 
              alignItems: 'center',
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px'
            }}>
              <div className="admin-view-label" style={{ 
                width: '150px', 
                fontWeight: '600',
                color: '#495057' 
              }}>Status</div>
              <div className="admin-view-value">
                <span className="admin-status-badge" style={{
                  ...getStatusStyle(member.status),
                  padding: '5px 12px',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  display: 'inline-block'
                }}>
                  {capitalizeFirstLetter(member.status)}
                </span>
              </div>
            </div>
          </div>
        </div>
          
        <div className="admin-card admin-card-elevated">
          <div className="admin-card-header admin-card-header-accent">
            <FontAwesomeIcon icon={faUser} className="admin-card-header-icon" />
            <h2 className="admin-card-title">Personal Information</h2>
          </div>
          <div className="admin-card-body">
            <div className="admin-view-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>First Name</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{member.firstName}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Last Name</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{member.lastName}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Email</div>
                <div className="admin-view-value admin-text-email" style={{
                  color: '#0366d6',
                  fontSize: '1rem',
                  wordBreak: 'break-word'
                }}>{member.email}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Phone</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{member.phone}</div>
              </div>
            </div>
          </div>
        </div>
          
        <div className="admin-card admin-card-elevated">
          <div className="admin-card-header admin-card-header-accent">
            <FontAwesomeIcon icon={faUser} className="admin-card-header-icon" />
            <h2 className="admin-card-title">Address Information</h2>
          </div>
          <div className="admin-card-body">
            <div className="admin-view-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Address</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{member.address || 'N/A'}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>City</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{member.city || 'N/A'}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>State</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{member.state || 'N/A'}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Pincode</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{parseInt(member.pincode) || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
          
        <div className="admin-card admin-card-elevated" style={{ marginBottom: '24px' }}>
          <div className="admin-card-header admin-card-header-accent">
            <FontAwesomeIcon icon={faBriefcase} className="admin-card-header-icon" />
            <h2 className="admin-card-title">Professional Information</h2>
          </div>
          <div className="admin-card-body">
            <div className="admin-view-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Profession</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{member.profession || 'N/A'}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Organization</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{member.organization || 'N/A'}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Designation</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{member.designation}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Experience</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{member.experience || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
          
        <div className="admin-card admin-card-elevated" style={{ marginBottom: '24px' }}>
          <div className="admin-card-header admin-card-header-accent">
            <FontAwesomeIcon icon={faIdCard} className="admin-card-header-icon" />
            <h2 className="admin-card-title">Membership Information</h2>
          </div>
          <div className="admin-card-body">
            <div className="admin-view-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Membership Type</div>
                <div className="admin-view-value admin-membership-type" style={{
                  color: '#343a40',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}>
                  {getMembershipTypeLabel(member.membership_type)}
                </div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Join Date</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{formatDate(member.join_date)}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Membership Validity</div>
                <div className="admin-view-value" style={{
                  color: member.expiry_date && new Date(member.expiry_date) < new Date() ? '#b2223b' : '#343a40',
                  fontSize: '1rem',
                  fontWeight: member.expiry_date && new Date(member.expiry_date) < new Date() ? '600' : 'normal'
                }}>{formatDate(member.expiry_date)}</div>
              </div>
              <div className="admin-view-item" style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <div className="admin-view-label" style={{
                  fontWeight: '600',
                  color: '#495057',
                  marginBottom: '5px',
                  fontSize: '0.9rem'
                }}>Last Updated</div>
                <div className="admin-view-value" style={{
                  color: '#343a40',
                  fontSize: '1rem'
                }}>{formatDate(member.updated_at)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default ViewMember;
